/**
 * Weather radar overlay using RainViewer tile API.
 * Adds a raster tile layer to the MapLibre map showing precipitation radar data.
 * Fetches the latest tile URLs from the RainViewer public API and renders them
 * as a raster source at 65% opacity.
 */

import type maplibregl from 'maplibre-gl';

const SOURCE_ID = 'weather-radar-source';
const LAYER_ID = 'weather-radar-layer';

/** RainViewer API response shape (subset of fields we use). */
interface RainViewerResponse {
  version: string;
  generated: number;
  host: string;
  radar: {
    past: Array<{ time: number; path: string }>;
    nowcast: Array<{ time: number; path: string }>;
  };
}

/** Cached tile path for the most recent radar frame. */
let cachedTilePath: string | null = null;
let cachedHost: string | null = null;

/**
 * Fetch the latest radar tile path from RainViewer.
 * Returns the full tile URL template suitable for a MapLibre raster source.
 */
async function fetchLatestTilePath(): Promise<string> {
  const response = await fetch('https://api.rainviewer.com/public/weather-maps.json', {
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    throw new Error(`RainViewer API returned ${response.status}`);
  }

  const data: RainViewerResponse = await response.json();

  // Use the most recent past frame (last item in the array)
  const pastFrames = data.radar.past;
  if (!pastFrames || pastFrames.length === 0) {
    throw new Error('No radar frames available from RainViewer');
  }

  const latestFrame = pastFrames[pastFrames.length - 1];
  cachedHost = data.host;
  cachedTilePath = latestFrame.path;

  // Color scheme 6, smooth=1, snow=1 — WM DeckGLMap.ts:969.
  return `${cachedHost}${cachedTilePath}/256/{z}/{x}/{y}/6/1_1.png`;
}

/**
 * Add the weather radar raster layer to the map.
 * Fetches the latest tile URL from RainViewer and adds a raster source + layer.
 */
export async function addWeatherRadarLayer(map: maplibregl.Map): Promise<void> {
  // Remove existing layer/source if present (idempotent)
  removeWeatherRadarLayer(map);

  const tileUrl = await fetchLatestTilePath();

  map.addSource(SOURCE_ID, {
    type: 'raster',
    tiles: [tileUrl],
    tileSize: 256,
    attribution: '&copy; <a href="https://www.rainviewer.com/">RainViewer</a>',
  });

  map.addLayer({
    id: LAYER_ID,
    type: 'raster',
    source: SOURCE_ID,
    paint: {
      'raster-opacity': 0.65,
    },
  });
}

/**
 * Remove the weather radar layer and source from the map.
 */
export function removeWeatherRadarLayer(map: maplibregl.Map): void {
  if (map.getLayer(LAYER_ID)) {
    map.removeLayer(LAYER_ID);
  }
  if (map.getSource(SOURCE_ID)) {
    map.removeSource(SOURCE_ID);
  }
}

/**
 * Update the weather radar to the latest available frame.
 * Swaps tile URLs in place via setTiles — removing and re-adding the source
 * mid-load flashes the whole overlay. Deferred to 'idle' while tiles are
 * still loading (WM DeckGLMap.ts:991-1002).
 */
export async function updateWeatherRadar(map: maplibregl.Map): Promise<void> {
  if (!map.getLayer(LAYER_ID)) {
    // Layer not active — nothing to update
    return;
  }

  const tileUrl = await fetchLatestTilePath();

  const swapTiles = (): void => {
    const source = map.getSource(SOURCE_ID) as maplibregl.RasterTileSource | undefined;
    if (source) source.setTiles([tileUrl]);
  };

  if (!map.isSourceLoaded(SOURCE_ID)) {
    map.once('idle', () => {
      try {
        swapTiles();
      } catch {
        // Source removed while waiting — nothing to update
      }
    });
    return;
  }

  swapTiles();
}
